import { z } from 'zod'
import type { FormControllerType } from '~/types/useForm'
import { DEFAULT_SETTINGS } from '~/utils/settings'

/** Un `FormData` es plano y `settings` es un jsonb anidado, así que los campos se
 *  nombran con prefijo (`issuerName`, `paymentBankName`…) y el árbol se vuelve a
 *  armar al guardar. El esquema valida sobre el plano, que es lo que hay en el DOM.
 */
const optionalEmail = (message: string) => z.union([z.literal(''), z.string().email(message)]).default('')

export const settingsSchema = z.object({
  name: z.string().trim().min(1, 'El nombre de la empresa es obligatorio'),
  slug: z.string().trim().default(''),
  domain: z.string().trim().default(''),

  currency: z.string().default('COP'),
  dateFormat: z.string().default('DD/MM/YYYY'),
  language: z.string().default('es'),
  notifyByEmail: z.boolean().default(true),
  notifyInApp: z.boolean().default(true),
  notificationEmail: optionalEmail('Correo de notificaciones inválido'),

  brandingAppName: z.string().default(''),
  brandingPrimaryColor: z.string().default(DEFAULT_SETTINGS.quoteBranding.primaryColor),
  brandingSecondaryColor: z.string().default(DEFAULT_SETTINGS.quoteBranding.secondaryColor),
  brandingFooterText: z.string().default(''),
  brandingLogoUrl: z.string().default(''),

  issuerName: z.string().trim().default(''),
  issuerDocument: z.string().trim().default(''),
  issuerPhone: z.string().trim().default(''),
  issuerEmail: optionalEmail('Correo del emisor inválido'),
  issuerAddress: z.string().trim().default(''),

  defaultsCurrency: z.string().default('COP'),
  defaultsTaxRate: z.coerce.number().min(0, 'El impuesto no puede ser negativo').default(0),
  defaultsValidDays: z.coerce.number().int('Deben ser días enteros').min(0, 'No puede ser negativo').default(15),
  defaultsTerms: z.string().default(''),

  paymentBankName: z.string().trim().default(''),
  paymentAccountType: z.string().default(''),
  paymentAccountNumber: z.string().trim().default(''),
  paymentAccountHolder: z.string().trim().default(''),
  paymentHolderDocument: z.string().trim().default(''),

  // Catálogos configurables (JSON en el formulario, arrays en settings). El
  // `Form` entrega los arrays ya parseados cuando el contenido es JSON válido,
  // y el string en blanco cuando el textarea está vacío: el esquema acepta ambos.
  currenciesJson: z.union([z.string(), z.array(z.unknown())]).default(''),
  documentTypesJson: z.union([z.string(), z.array(z.unknown())]).default(''),
  customerStatesJson: z
    .array(
      z.object({
        key: z.string().min(1, 'Cada etapa necesita una clave'),
        label: z.string().min(1, 'Cada etapa necesita un nombre'),
        color: z.string().default('default'),
        order: z.number()
      })
    )
    .superRefine((stages, ctx) => {
      const seen = new Set<string>()
      for (const stage of stages) {
        if (seen.has(stage.key)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Clave repetida: "${stage.key}"` })
          break
        }
        seen.add(stage.key)
      }
    })
    .default([]),
  bankAccountTypesJson: z.union([z.string(), z.array(z.unknown())]).default(''),
  addressLabelsJson: z.union([z.string(), z.array(z.unknown())]).default(''),
  customerClassificationsJson: z.union([z.string(), z.array(z.unknown())]).default(''),

  numberLocale: z.string().default('es-CO'),
  dateLocale: z.string().default('es-CO'),
  country: z.string().default('CO')
})

export type SettingsFormType = z.infer<typeof settingsSchema>

/** Lo que comparten todas las pestañas: el controlador del formulario único. */
export interface EmpresaTabBaseProps {
  form: FormControllerType<SettingsFormType>
}

export interface TabGeneralProps extends EmpresaTabBaseProps {}

export interface TabFormatosProps extends EmpresaTabBaseProps {
  currencyOptions: () => Array<{ value: string; title: string }>
}

export interface TabMarcaProps extends EmpresaTabBaseProps {}

export interface TabCotizacionesProps extends EmpresaTabBaseProps {
  logoUrl: () => string
  handleLogoFile: (file?: File) => void
  currencyOptions: () => Array<{ value: string; title: string }>
}

export interface TabPagosProps extends EmpresaTabBaseProps {}

export interface TabCatalogosProps extends EmpresaTabBaseProps {}

export interface TabCrmProps extends EmpresaTabBaseProps {}
